import type { ReactNode } from "react";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { META_CLS } from "@/components/ui/escala-panel";
import { cn } from "@/lib/utils";

/**
 * Encabezado estándar de página para las pantallas que viven en AdminLayout (Usuarios, Usuarios
 * Clientes, Ayuda de las apps, Asistentes IA…). Los portales (Productos, Cobranza) pintan su título
 * en la barra del layout con esta misma tipografía; AdminLayout no lo hace, así que la página lo
 * pone con este componente y todas se ven igual.
 *
 * Reglas del estándar:
 * - Título `text-xl font-bold tracking-tight`, sin ícono.
 * - Sin subtítulo largo: la explicación va en `ayuda` (tooltip estándar junto al título).
 * - El conteo de filas NO va aquí: va arriba de la tabla (`TableCount` / `DataTable`).
 * - Acciones de la página a la derecha, en tamaño `sm`.
 * - `detalle`: una línea de datos cortos para fichas (código, ruta, modelo), en `META_CLS`.
 * - `antes`: lo que va arriba del título, p. ej. el botón "volver" de un detalle.
 */
export function EncabezadoPagina({
  titulo,
  ayuda,
  insignias,
  detalle,
  acciones,
  antes,
  className,
}: {
  titulo: ReactNode;
  ayuda?: ReactNode;
  /** Pastillas junto al título (estado de la ficha, etc.). */
  insignias?: ReactNode;
  detalle?: ReactNode;
  acciones?: ReactNode;
  antes?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-1", className)}>
      {antes}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h1 className="min-w-0 break-words text-xl font-bold tracking-tight text-foreground">{titulo}</h1>
            {ayuda && <InfoTooltip content={ayuda} iconClassName="h-3.5 w-3.5" />}
            {insignias}
          </div>
          {detalle && <p className={cn(META_CLS, "mt-0.5")}>{detalle}</p>}
        </div>
        {acciones && <div className="flex shrink-0 flex-wrap items-center gap-2">{acciones}</div>}
      </div>
    </div>
  );
}
